<script setup lang="ts" generic="TStudent extends Components.Schemas.StudentDto | Components.Schemas.BaseStudentDto">
import { Components } from '@/api/generated/api';
import ClassStudentsListItem from '@/core/list-items/ClassStudentsListItem.vue';
import StudentListItem from '@/core/list-items/StudentListItem.vue';
import SearchSelect from '@/core/selects/SearchSelect.vue';
import { matchesAllSearchWords } from 'common/utils/string.utils';
import { computed, toRefs } from 'vue';

const props = defineProps<{
  students: TStudent[];
  placeholder: string;
  classOption?: { schoolClass: Components.Schemas.SchoolClassDto; studentCount: number } | null;
}>();
const { students } = toRefs(props);
const showCurrentClassOption = computed<boolean>(() => {
  return props.classOption != null && students.value.length > 0;
});

const emit = defineEmits<{
  'student-selected': [value: TStudent | null | 'all'];
}>();

const filterStudents = (data: TStudent[], query: string): TStudent[] => {
  if (!query) return data;
  return data.filter((student) => {
    return matchesAllSearchWords([student.firstName, student.lastName], query);
  });
};
</script>

<template>
  <SearchSelect
    :options="students"
    :placeholder="placeholder"
    :can-select-all="showCurrentClassOption"
    :empty-message="$t('general.noResults')"
    :filter-callback="filterStudents"
    @option-selected="emit('student-selected', $event)"
  >
    <template v-if="classOption && showCurrentClassOption" #all="{ active }">
      <ClassStudentsListItem
        :student-count="classOption.studentCount"
        :school-class="classOption.schoolClass"
        type="add"
        class="bg-white state-effects"
        :class="{
          '!bg-primary/20': active,
        }"
      />
    </template>
    <template #default="{ option, active }">
      <StudentListItem
        :student="option"
        type="add"
        class="bg-white state-effects"
        :class="{
          '!bg-primary/20': active,
        }"
      />
    </template>
  </SearchSelect>
</template>
